import React from 'react';
import { CheckCircle2, Target, Eye, Lightbulb, Shield, Users, TrendingUp, BookOpen } from 'lucide-react';

const About = () => {
  const coreValues = [
    { icon: Lightbulb, title: 'Innovation', color: 'text-blue-600' },
    { icon: Shield, title: 'Integrity', color: 'text-green-600' },
    { icon: Target, title: 'Customer Success', color: 'text-cyan-600' },
    { icon: TrendingUp, title: 'Excellence', color: 'text-purple-600' },
    { icon: Users, title: 'Collaboration', color: 'text-orange-600' },
    { icon: BookOpen, title: 'Continuous Learning', color: 'text-pink-600' },
  ];

  return (
    <section id="about" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Who We Are
          </h2>
          <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
          <p className="text-2xl font-semibold text-gray-700 max-w-4xl mx-auto">
            Empowering Businesses Through Technology, Innovation & Trust
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left Content */}
          <div>
            <p className="text-gray-600 text-lg mb-6 leading-relaxed">
              At <span className="font-semibold text-gray-900">Adidar Tech India Pvt. Ltd.</span>, we help organizations accelerate their digital transformation by delivering innovative, secure, and scalable technology solutions. Our expertise spans cloud computing, artificial intelligence, enterprise software development, IT consulting, cybersecurity, and managed technology services.
            </p>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              With a customer-first approach and a commitment to excellence, we partner with businesses of all sizes to simplify complex challenges, optimize operations, and drive sustainable growth in an ever-evolving digital world.
            </p>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/team-meeting.png"
                alt="Adidar Tech Team - Professional collaboration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Mission and Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* Mission */}
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
            </div>
            <p className="text-gray-700 leading-relaxed text-lg">
              To empower businesses with intelligent, reliable, and future-ready technology solutions that enhance productivity, foster innovation, and create lasting business value.
            </p>
          </div>

          {/* Vision */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
            </div>
            <p className="text-gray-700 leading-relaxed text-lg">
              To become a globally trusted technology partner, recognized for innovation, integrity, and excellence in delivering transformative digital solutions that shape the future.
            </p>
          </div>
        </div>

        {/* Core Values */}
        <div className="bg-gray-50 p-10 rounded-2xl">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-900 mb-3">Our Core Values</h3>
            <p className="text-gray-600 text-lg">The principles that guide everything we do</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {coreValues.map((value) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  className="flex flex-col items-center text-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1"
                >
                  <Icon className={`w-10 h-10 ${value.color} mb-3`} />
                  <h4 className="text-gray-900 font-semibold text-sm">{value.title}</h4>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;